import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import LoadingLogo from "@/components/LoadingLogo";
import {CarouselGameCard, normalizeCarouselGame} from "@/components";
import {useGetHotGames} from "@/hooks/gamesList/useGetHotGames";
import {useAuthStore} from "@/store/useAuth.store";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";
import {Carousel} from "../../../../../../../components/Carousel";
import {HeadingCarousel} from "../../../../../../../components/heading-carousel";
import styles from "./top-paying-games.styles.module.scss";

const SKELETON_KEYS = [
  "top-paying-skel-0",
  "top-paying-skel-1",
  "top-paying-skel-2",
  "top-paying-skel-3",
  "top-paying-skel-4",
  "top-paying-skel-5",
];

export const TopPayingGames = () => {
  const {t} = useTranslation();
  const {games, isPending} = useGetHotGames();
  const [carouselApi, setCarouselApi] = useState(null);

  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);
  const initializeGames = useGameMetadataStore(state => state.initializeGames);
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesLoading = useGameMetadataStore(state => state.loading);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);

  useEffect(() => {
    if (!gamesInitialized && !gamesLoading) {
      initializeGames();
    }
  }, [gamesInitialized, gamesLoading, initializeGames]);

  const displayGames = useMemo(
    () => games.map(game => normalizeCarouselGame(game, getGameById)),
    [games, getGameById]
  );

  const handleGameClick = useCallback(
    (provider, gameId) => {
      if (authStatus === "authenticated") {
        navigate(`/games/${provider}/${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  return (
    <>
      {isPending ? (
        <div>
          <LoadingLogo />
        </div>
      ) : games.length > 0 ? (
        <div
          id="topPayingGames"
          className={styles.container}
        >
          <HeadingCarousel
            title={t("topPayingGamesTitle")}
            subtitle={`${t("topPayingGamesSubTitle")} 🔥`}
            icon="🤑"
            viewAllLink="/casino"
            showArrowsOnMobile
            onPrev={carouselApi?.scrollPrev}
            onNext={carouselApi?.scrollNext}
          />
          <Carousel
            autoScroll={false}
            onInit={setCarouselApi}
            renderItems={() =>
              isPending || games.length === 0
                ? SKELETON_KEYS.map(skelKey => (
                    <div key={skelKey}>
                      <CarouselGameCard loading />
                    </div>
                  ))
                : displayGames.map((game, index) => (
                    <div key={games[index].uniqueId}>
                      <CarouselGameCard
                        name={game.name}
                        icon={game.icon}
                        providerLabel={game.providerLabel}
                        badge="hot"
                        onClick={() => handleGameClick(game.provider, game.id)}
                      />
                    </div>
                  ))
            }
          />
        </div>
      ) : null}
    </>
  );
};
